/**
 * Intake Response Review Drawer Component
 * 
 * Slide-out drawer for reviewing submitted patient intake responses.
 * Shows formatted responses, calculated scores, risk flags, and allows
 * marking as reviewed and triggering IntelliHealth analysis.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Loader2,
  FileText,
  User,
  Calendar,
  ClipboardCheck,
  AlertCircle,
  Download
} from 'lucide-react';
import { 
  getResponseDetails, 
  markResponseAsReviewed,
  getTherapyCategoryLabel,
  downloadResponsePDF
} from '@/services/intakeService';
import { useToast } from '@/hooks/use-toast';
import { notifyIntakeReviewed } from '@/hooks/usePendingIntakes';
import { getIntakeTemplate, therapyCategoryToTemplateType } from '@/lib/intake/templates';

interface IntakeResponseReviewDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  responseId: string | null;
  onReviewed: () => void;
  onRunIntelliHealth?: () => void;
}

interface CustomTemplateQuestion {
  id: string;
  text: string;
  inputType: string;
  options?: string[];
}
interface CustomTemplateSection {
  id: string;
  title: string;
  questions: CustomTemplateQuestion[];
}
interface CustomTemplateSchema {
  sections: CustomTemplateSection[];
}

interface ResponseData {
  id: string;
  intakeType: string;
  therapyCategory: string;
  responses: Record<string, unknown> | Array<{ questionId: string; value: unknown }> | { stages: any[] }; // Backend returns object, stages array, or we flatten to array
  scores: Record<string, number>;
  riskFlags?: Record<string, boolean>;
  completedAt: string;
  isReviewed: boolean;
  reviewedAt?: string;
  reviewNotes?: string;
  reviewedBy?: { id: string; name: string };
  intake?: {
    id: string;
    patientName: string;
    patientEmail: string;
    createdAt: string;
  };
  patient?: {
    id: string;
    name: string;
    dateOfBirth: string;
    gender: string;
  };
  /** Present when the response was submitted against a clinic-authored custom form. */
  intakeFormTemplate?: {
    id: string;
    name: string;
    schema: CustomTemplateSchema;
  } | null;
}

// Question ID to readable label mapping
const questionLabels: Record<string, string> = {
  // ADAM Questionnaire (questionnaires.adam.* format from PatientIntake)
  'questionnaires.adam.q1_decreased_libido': 'Decreased sex drive/libido',
  'questionnaires.adam.q2_lack_of_energy': 'Lack of energy',
  'questionnaires.adam.q3_decreased_strength': 'Decrease in strength/endurance',
  'questionnaires.adam.q4_lost_height': 'Lost height',
  'questionnaires.adam.q5_decreased_enjoyment': 'Decreased enjoyment of life',
  'questionnaires.adam.q6_sad_or_grumpy': 'Sad or grumpy',
  'questionnaires.adam.q7_weaker_erections': 'Erections less strong',
  'questionnaires.adam.q8_sports_performance_decline': 'Deterioration in sports ability',
  'questionnaires.adam.q9_falling_asleep_after_dinner': 'Falling asleep after dinner',
  'questionnaires.adam.q10_work_performance_decline': 'Deterioration in work performance',
  
  // ADAM Questionnaire (adam.* format from Men's TRT Intake)
  'adam.q1_decreased_libido': 'Decreased sex drive/libido',
  'adam.q2_lack_of_energy': 'Lack of energy',
  'adam.q3_decreased_strength': 'Decrease in strength/endurance',
  'adam.q4_lost_height': 'Lost height',
  'adam.q5_decreased_enjoyment': 'Decreased enjoyment of life',
  'adam.q6_sad_or_grumpy': 'Sad or grumpy',
  'adam.q7_weaker_erections': 'Erections less strong',
  'adam.q8_sports_performance_decline': 'Deterioration in sports ability',
  'adam.q9_falling_asleep_after_dinner': 'Falling asleep after dinner',
  'adam.q10_work_performance_decline': 'Deterioration in work performance',
  
  // STOP-Bang (questionnaires.stopBang.* format from PatientIntake)
  'questionnaires.stopBang.snore': 'Loud snoring',
  'questionnaires.stopBang.tired': 'Often tired during day',
  'questionnaires.stopBang.observed_apneas': 'Observed stopped breathing',
  'questionnaires.stopBang.high_blood_pressure': 'High blood pressure',
  'questionnaires.stopBang.bmi_over_35': 'BMI over 35',
  'questionnaires.stopBang.age_over_50': 'Age over 50',
  'questionnaires.stopBang.neck_over_40cm': 'Neck circumference over 40cm',
  'questionnaires.stopBang.male_gender': 'Male gender',
  
  // STOP-Bang (stopBang.* format from Men's TRT Intake)
  'stopBang.snore': 'Loud snoring',
  'stopBang.tired': 'Often tired during day',
  'stopBang.observed_apneas': 'Observed stopped breathing',
  'stopBang.high_blood_pressure': 'High blood pressure',
  'stopBang.bmi_over_35': 'BMI over 35',
  'stopBang.age_over_50': 'Age over 50',
  'stopBang.neck_over_40cm': 'Neck circumference over 40cm',
  'stopBang.male_gender': 'Male gender',
  // Basic Info (snake_case format)
  'basic_info.first_name': 'First Name',
  'basic_info.last_name': 'Last Name',
  'basic_info.email': 'Email',
  'basic_info.phone': 'Phone',
  'basic_info.date_of_birth': 'Date of Birth',
  'basic_info.biological_sex': 'Biological Sex',
  'basic_info.city': 'City',
  'basic_info.state': 'State',
  'basic_info.zip_code': 'ZIP Code',
  // Lifestyle (snake_case format)
  'lifestyle.exercise_frequency': 'Exercise Frequency',
  'lifestyle.diet_quality': 'Diet Quality',
  'lifestyle.sleep_hours': 'Sleep Hours',
  'lifestyle.stress_level': 'Stress Level',
  'lifestyle.smoking_status': 'Smoking Status',
  'lifestyle.alcohol_frequency': 'Alcohol Frequency',
  'lifestyle.goals': 'Treatment Goals',
  // Medical History
  'medical_history.conditions': 'Medical Conditions',
  'medical_history.medications': 'Current Medications',
  'medical_history.allergies': 'Allergies',
  'medical_history.surgeries': 'Surgeries',
  'medical_history.family_history': 'Family History',
  
  // Men's/Women's Intake Forms - camelCase fields (top level)
  fullName: 'Full Name',
  email: 'Email',
  phone: 'Phone Number',
  dateOfBirth: 'Date of Birth',
  height: 'Height',
  weight: 'Weight',
  state: 'State',
  selectedGoals: 'Selected Goals',
  
  // Lifestyle fields (camelCase from Men's/Women's forms)
  activityLevel: 'Activity Level',
  exerciseFrequency: 'Exercise Frequency',
  sleepHours: 'Sleep Hours',
  sleepQuality: 'Sleep Quality',
  stressLevel: 'Stress Level',
  alcoholConsumption: 'Alcohol Consumption',
  tobaccoUse: 'Tobacco Use',
  diet: 'Diet',
  caffeineCups: 'Caffeine (cups/day)',
  alcoholDrinks: 'Alcohol (drinks/week)',
  
  // Medical History (camelCase from Men's/Women's forms)
  currentMedications: 'Current Medications',
  allergies: 'Allergies',
  chronicConditions: 'Chronic Conditions',
  surgeries: 'Surgeries',
  familyHistory: 'Family History',
  previousTRT: 'Previous TRT',
  previousTRTDetails: 'Previous TRT Details',
  previousHRT: 'Previous HRT',
  previousHRTDetails: 'Previous HRT Details',
  menopausalStatus: 'Menopausal Status',
  birthControlCurrent: 'Currently on Birth Control',
  pregnancies: 'Number of Pregnancies',
  hormoneTherapy: 'Previous Hormone Therapy',
  
  // Women's form specific
  age: 'Age',
  preferredContactMethod: 'Preferred Contact Method',
  preferredContactTime: 'Preferred Contact Time',
  additionalConcerns: 'Additional Concerns',
  symptoms: 'Symptoms',
  
  // Women's intake - additional fields
  smoking: 'Smoking/Tobacco Use',
  medications: 'Medications',
  hasAllergies: 'Has Allergies',
  allergiesDetails: 'Allergy Details',
  medicalConditions: 'Medical Conditions',
  additionalMedicalHistory: 'Additional Medical History',
  previousHormoneTherapy: 'Previous Hormone Therapy',
  hormoneTherapyDetails: 'Hormone Therapy Details',
  
  // Women's intake - preferences
  budget: 'Budget',
  refillFrequency: 'Refill Frequency',
  consultationMethod: 'Consultation Method',
  programRecommended: 'Preferred Delivery Method',
  additionalPreferences: 'Additional Preferences',
  
  // Women's intake - symptoms (nested in symptoms object)
  'symptoms.hot_flashes': 'Hot Flashes',
  'symptoms.night_sweats': 'Night Sweats',
  'symptoms.cold_intolerance': 'Cold Intolerance',
  'symptoms.mood_swings': 'Mood Swings',
  'symptoms.anxiety': 'Anxiety',
  'symptoms.depression': 'Depression',
  'symptoms.irritability': 'Irritability',
  'symptoms.fatigue': 'Fatigue',
  'symptoms.joint_pain': 'Joint Pain',
  'symptoms.muscle_aches': 'Muscle Aches',
  'symptoms.weight_gain': 'Weight Gain',
  'symptoms.breast_tenderness': 'Breast Tenderness',
  'symptoms.irregular_cycles': 'Irregular Cycles',
  'symptoms.decreased_libido': 'Decreased Libido',
  'symptoms.insomnia': 'Insomnia',
  'symptoms.frequent_waking': 'Frequent Waking',
  'symptoms.unrefreshing_sleep': 'Unrefreshing Sleep',
  'symptoms.brain_fog': 'Brain Fog',
  'symptoms.memory_issues': 'Memory Issues',
  'symptoms.vaginal_dryness': 'Vaginal Dryness',
  'symptoms.hair_loss': 'Hair Loss',
  'symptoms.skin_changes': 'Skin Changes',
  
  // Legacy format (backwards compatibility)
  adam_q1_decreased_libido: 'Decreased sex drive/libido',
  adam_q2_lack_of_energy: 'Lack of energy',
  adam_q3_decreased_strength: 'Decrease in strength/endurance',
  adam_q4_lost_height: 'Lost height',
  adam_q5_decreased_enjoyment: 'Decreased enjoyment of life',
  adam_q6_sad_grumpy: 'Sad or grumpy',
  adam_q7_erections_less_strong: 'Erections less strong',
  adam_q8_deterioration_sports: 'Deterioration in sports ability',
  adam_q9_falling_asleep: 'Falling asleep after dinner',
  adam_q10_deterioration_work: 'Deterioration in work performance',
  stopbang_snoring: 'Loud snoring',
  stopbang_tired: 'Often tired during day',
  stopbang_observed: 'Observed stopped breathing',
  stopbang_pressure: 'High blood pressure',
  stopbang_bmi: 'BMI over 35',
  stopbang_age: 'Age over 50',
  stopbang_neck: 'Neck circumference over 40cm',
  stopbang_gender: 'Male gender',
  current_medications: 'Current Medications',
  medical_conditions: 'Medical Conditions',
  family_history: 'Family History',
  exercise_frequency: 'Exercise Frequency',
  diet_description: 'Diet Description',
  sleep_hours: 'Sleep Hours',
  alcohol_use: 'Alcohol Use',
  tobacco_use: 'Tobacco Use',
  stress_level: 'Stress Level',
  treatment_goals: 'Treatment Goals',
  
  // GLP1 / Weight Loss specific fields
  currentWeight: 'Current Weight',
  goalWeight: 'Goal Weight',
  weightLossGoal: 'Weight Loss Goal',
  weightDistribution: 'Weight Distribution',
  previousAttempts: 'Previous Weight Loss Attempts',
  currentGlp1Type: 'Current GLP-1 Type',
  currentGlp1Dose: 'Current GLP-1 Dose',
  currentGlp1Duration: 'Current GLP-1 Duration',
  currentGlp1SideEffects: 'Current GLP-1 Side Effects',
  metabolicConditions: 'Metabolic Conditions',
  contraindications: 'Contraindications',
  kidneyConditions: 'Kidney Conditions',
  pregnancyStatus: 'Pregnancy Status',
  lastProviderVisit: 'Last Provider Visit',
  glp1Consent: 'GLP-1 Consent',
  bloodPressureSystolic: 'Blood Pressure (Systolic)',
  bloodPressureDiastolic: 'Blood Pressure (Diastolic)',
  bloodPressureUnknown: 'Blood Pressure Unknown',
  
  // Shipping & Billing address fields — nested format (SmartIntake / General Health)
  'shipping_billing.shippingAddressLine1': 'Shipping Street Address',
  'shipping_billing.shippingAddressLine2': 'Shipping Apt / Suite / Unit',
  'shipping_billing.shippingCity': 'Shipping City',
  'shipping_billing.shippingState': 'Shipping State',
  'shipping_billing.shippingPostalCode': 'Shipping ZIP Code',
  'shipping_billing.billingAddressLine1': 'Billing Street Address',
  'shipping_billing.billingAddressLine2': 'Billing Apt / Suite / Unit',
  'shipping_billing.billingCity': 'Billing City',
  'shipping_billing.billingState': 'Billing State',
  'shipping_billing.billingPostalCode': 'Billing ZIP Code',
  'shipping_billing.billingSameAsShipping': 'Is your billing address the same as your shipping address?',

  // Additional personal info fields
  firstName: 'First Name',
  lastName: 'Last Name',
  addressLine1: 'Address Line 1',
  addressLine2: 'Address Line 2',
  city: 'City',
  postalCode: 'Postal Code',
  telehealthLocationState: 'Telehealth Location State',
  governmentIdKey: 'Government ID',
  
  // Additional fields
  gender: 'Gender',
  biologicalSex: 'Biological Sex',
};

type TemplateQuestionInfo = {
  text: string;
  sectionTitle: string;
  sectionIndex: number;
  questionIndex: number;
  inputType?: string;
  options?: string[];
};

type TemplateLookup = {
  questionMap: Map<string, TemplateQuestionInfo>;
  sectionOrder: Map<string, number>;
};

type FlattenedResponse = {
  questionId: string;
  value: unknown;
  stageName?: string;
  questionText?: string;
  questionOrder?: number;
  sectionTitle?: string;
  sectionOrder?: number;
};

const STOP_BANG_KEY_MAP: Record<string, string> = {
  snore: 'stopbang_snore',
  tired: 'stopbang_tired',
  observed_apneas: 'stopbang_observed',
  high_blood_pressure: 'stopbang_pressure',
  bmi_over_35: 'stopbang_bmi',
  age_over_50: 'stopbang_age',
  neck_over_40cm: 'stopbang_neck',
  male_gender: 'stopbang_male',
};

// These fields can be persisted in some payloads even when they were not
// asked in a specific intake template. We only hide them when they are empty
// placeholders and the active template does not define them.
const CONDITIONAL_RESPONSE_PATHS = new Set([
  'basic_info.city',
  'basic_info.state',
  'basic_info.zip_code',
]);

// Legacy/deprecated fields that should never be shown in the response review.
const EXCLUDED_RESPONSE_PATHS = new Set([
  'menopausalStatus',
  'menopausal_status',
  'birthControlCurrent',
  'pregnancies',
  'hormoneTherapy',
  'hasAllergies',
  'allergiesDetails',
]);

// Flat address keys that are redundant when shipping_billing.* is present.
// These come from the form state / template responses but should not be
// displayed — the canonical source for addresses is the shipping_billing
// nested object.  Covers both camelCase and snake_case variants.
const FLAT_ADDRESS_FIELDS = new Set([
  // camelCase (WeightLossIntake / page-based intakes)
  'shippingAddressLine1', 'shippingAddressLine2', 'shippingCity', 'shippingState', 'shippingPostalCode',
  'billingAddressLine1', 'billingAddressLine2', 'billingCity', 'billingState', 'billingPostalCode',
  'billingSameAsShipping',
  // snake_case (template question IDs stored alongside shipping_billing by SmartIntake)
  'shipping_address_line1', 'shipping_address_line2', 'shipping_city', 'shipping_state', 'shipping_postal_code',
  'billing_address_line1', 'billing_address_line2', 'billing_city', 'billing_state', 'billing_postal_code',
  'billing_same_as_shipping',
]);

const WOMENS_GOAL_LABELS: Record<string, string> = {
  vasomotor: 'Reduce hot flashes/night sweats',
  sleep: 'Improve sleep quality',
  libido: 'Boost libido/sexual wellness',
  mood: 'Stabilize mood/anxiety',
  energy: 'Enhance energy & focus',
  metabolic: 'Metabolic/weight support',
  vaginal_comfort: 'Vaginal comfort',
};

const MENS_GOAL_LABELS: Record<string, string> = {
  energy: 'Boost energy levels',
  libido: 'Improve libido/sexual health',
  strength: 'Build strength/muscle',
  mood: 'Stabilize mood',
  sleep: 'Improve sleep quality',
  focus: 'Sharpen mental clarity',
  weight: 'Metabolic support',
};

const toSnakeCase = (value: string): string => {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .replace(/\./g, '_')
    .toLowerCase();
};

const normalizeQuestionIdCandidates = (questionId: string): string[] => {
  const candidates = new Set<string>();
  const raw = questionId || '';
  const stripped = raw.replace(/^questionnaires\./, '');
  const lastPart = raw.split('.').pop() || raw;

  candidates.add(stripped);
  candidates.add(stripped.replace(/\./g, '_'));
  candidates.add(lastPart);
  candidates.add(toSnakeCase(lastPart));
  candidates.add(toSnakeCase(stripped));
  candidates.add(toSnakeCase(raw));

  const [group, ...restParts] = stripped.split('.');
  const rest = restParts.join('.');
  const groupLower = group?.toLowerCase();

  if (groupLower === 'adam' && rest) {
    candidates.add(`adam_${rest}`);
    candidates.add(`adam_${rest.replace(/\./g, '_')}`);
  }

  if (groupLower === 'stopbang' && rest) {
    const restLower = rest.toLowerCase();
    const mapped = STOP_BANG_KEY_MAP[restLower];
    if (mapped) candidates.add(mapped);
    candidates.add(`stopbang_${restLower}`);
  }

  return Array.from(candidates).map(candidate => candidate.toLowerCase());
};

const buildTemplateLookup = (
  therapyCategory?: string,
  gender?: string | number
): TemplateLookup | null => {
  if (!therapyCategory) return null;
  const templateType = therapyCategoryToTemplateType(therapyCategory, gender);
  const template = getIntakeTemplate(templateType);
  const questionMap = new Map<string, TemplateQuestionInfo>();
  const sectionOrder = new Map<string, number>();

  template.sections.forEach((section, sectionIndex) => {
    sectionOrder.set(section.title, sectionIndex);
    section.questions.forEach((question, questionIndex) => {
      questionMap.set(question.id.toLowerCase(), {
        text: question.text,
        sectionTitle: section.title,
        sectionIndex,
        questionIndex,
        inputType: question.inputType,
        options: question.options,
      });
    });
  });

  return { questionMap, sectionOrder };
};

const buildCustomTemplateLookup = (
  schema: CustomTemplateSchema | undefined | null
): TemplateLookup | null => {
  if (!schema || !Array.isArray(schema.sections)) return null;
  const questionMap = new Map<string, TemplateQuestionInfo>();
  const sectionOrder = new Map<string, number>();

  schema.sections.forEach((section, sectionIndex) => {
    sectionOrder.set(section.title, sectionIndex);
    (section.questions || []).forEach((question, questionIndex) => {
      questionMap.set(question.id.toLowerCase(), {
        text: question.text,
        sectionTitle: section.title,
        sectionIndex,
        questionIndex,
        inputType: question.inputType as TemplateQuestionInfo['inputType'],
        options: question.options,
      });
    });
  });

  return { questionMap, sectionOrder };
};

const findTemplateQuestionInfo = (
  questionId: string,
  templateLookup: TemplateLookup | null
): TemplateQuestionInfo | null => {
  if (!templateLookup) return null;
  const candidates = normalizeQuestionIdCandidates(questionId);
  for (const candidate of candidates) {
    const info = templateLookup.questionMap.get(candidate);
    if (info) return info;
  }
  return null;
};

// Helper to format a key into readable label
const formatKeyToLabel = (key: string): string => {
  if (!key) return 'Unknown';
  if (questionLabels[key]) return questionLabels[key];
  
  const lastPart = key.split('.').pop() || key;
  return lastPart
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export const IntakeResponseReviewDrawer: React.FC<IntakeResponseReviewDrawerProps> = ({
  open,
  onOpenChange,
  responseId,
  onReviewed,
  onRunIntelliHealth
}) => {
  const [data, setData] = useState<ResponseData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');
  const { toast } = useToast();
  const templateLookup = useMemo(
    () => (
      buildCustomTemplateLookup(data?.intakeFormTemplate?.schema)
      ?? buildTemplateLookup(data?.therapyCategory, data?.patient?.gender)
    ),
    [data?.intakeFormTemplate?.schema, data?.therapyCategory, data?.patient?.gender]
  );

  useEffect(() => {
    if (open && responseId) {
      loadResponseDetails();
    }
  }, [open, responseId]);

  const loadResponseDetails = async () => {
    if (!responseId) return;
    
    setIsLoading(true);
    try {
      const result = await getResponseDetails(responseId);
      if (result.success && result.data) {
        setData(result.data);
        
        // Auto-mark as reviewed when viewed (if not already reviewed)
        // if (!result.data.isReviewed) {
        //   try {
        //     await markResponseAsReviewed(responseId);
        //     // Refresh the parent data to update badge counts
        //     onReviewed();
        //   } catch {
        //     // Silently fail - viewing is still successful
        //   }
        // }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to load response details',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while loading response details',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!responseId) return;
    
    setIsDownloadingPDF(true);
    try {
      await downloadResponsePDF(responseId);
      toast({
        title: 'PDF Downloaded',
        description: 'The intake response PDF has been downloaded.',
      });
    } catch (error) {
      toast({
        title: 'Download Failed',
        description: 'Failed to download the PDF. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloadingPDF(false);
    }
  };

  const handleMarkReviewed = async () => {
    if (!responseId) return;
    
    setIsSubmitting(true);
    try {
      const result = await markResponseAsReviewed(responseId, reviewNotes || undefined);
      if (result.success) {
        toast({
          title: 'Response Reviewed',
          description: 'The response has been marked as reviewed.',
        });
        // Notify the sidebar badge to refresh immediately
        notifyIntakeReviewed();
        onReviewed();
        onOpenChange(false);
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to mark response as reviewed',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'An error occurred while marking response as reviewed',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getScoreColor = (scoreName: string, value: number) => {
    if (scoreName === 'adam') {
      return value >= 4 ? 'text-amber-400 bg-amber-500/10' : 'text-green-400 bg-green-500/10';
    }
    if (scoreName === 'stopBang' || scoreName === 'stop_bang') {
      if (value >= 5) return 'text-red-400 bg-red-500/10';
      if (value >= 3) return 'text-amber-400 bg-amber-500/10';
      return 'text-green-400 bg-green-500/10';
    }
    return 'text-blue-400 bg-blue-500/10';
  };

  const getScoreLabel = (scoreName: string, value: number) => {
    if (scoreName === 'adam') {
      return value >= 4 ? 'Positive Screen' : 'Negative Screen';
    }
    if (scoreName === 'stopBang' || scoreName === 'stop_bang') {
      if (value >= 5) return 'High Risk';
      if (value >= 3) return 'Intermediate Risk';
      return 'Low Risk';
    }
    return '';
  };

  // Symptom severity labels (0-4 scale from Women's intake form)
  const SEVERITY_LABELS = ['None', 'Mild', 'Moderate', 'Severe', 'Very Severe'];
  
  // Check if a value is an S3 image key
  const isS3ImageKey = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return value.startsWith('public-intake/') && 
           (value.endsWith('.jpg') || value.endsWith('.jpeg') || value.endsWith('.png') || value.endsWith('.gif') || value.endsWith('.webp'));
  };

  const isUrl = (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return /^https?:\/\/.+/i.test(value.trim());
  };

  // Handle viewing S3 image - fetch presigned URL and open in new tab
  const handleViewS3Image = async (key: string) => {
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${baseUrl}/uploads/view-url?key=${encodeURIComponent(key)}`);
      const result = await response.json();
      
      if (result.success && result.data?.viewUrl) {
        window.open(result.data.viewUrl, '_blank');
      } else {
        toast({
          title: 'Error',
          description: 'Failed to get image URL',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load image',
        variant: 'destructive',
      });
    }
  };

  const formatQuestionValue = (value: unknown, questionId?: string): string | React.ReactNode => {
    // Handle null/undefined/empty values - show as "Not answered" instead of "—"
    if (value === null || value === undefined) {
      return <span className="text-white/30 italic">Not answered</span>;
    }

    // Legacy data corruption: address/file objects on older custom-intake
    // submissions were stringified to "[object Object]" before persistence
    // (fixed in buildStagesPayloadFromCustomTemplate). Surface those as
    // unanswered rather than the raw garbage string.
    if (typeof value === 'string' && value.trim() === '[object Object]') {
      return <span className="text-white/30 italic">Not answered</span>;
    }

    const resolveOptionLabel = (rawValue: unknown) => {
      if (!questionId) return null;
      const templateInfo = findTemplateQuestionInfo(questionId, templateLookup);
      const options = templateInfo?.options;
      if (!options || options.length === 0) return null;

      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        const exactMatch = options.find(option => option === trimmed);
        if (exactMatch) return exactMatch;
        const loweredMatch = options.find(option => option.toLowerCase() === trimmed.toLowerCase());
        if (loweredMatch) return loweredMatch;
      }

      const numericValue =
        typeof rawValue === 'number'
          ? rawValue
          : typeof rawValue === 'string' && rawValue.trim() !== '' && !isNaN(Number(rawValue))
            ? Number(rawValue)
            : null;

      if (numericValue === null) return null;

      const zeroBased = options[numericValue];
      if (zeroBased !== undefined) return zeroBased;

      const oneBased = options[numericValue - 1];
      if (oneBased !== undefined) return oneBased;

      const stringMatch = options.find(option => option === String(numericValue));
      if (stringMatch) return stringMatch;

      return null;
    };

    const resolveGoalLabel = (rawGoal: string) => {
      const templateType = data ? therapyCategoryToTemplateType(data.therapyCategory, data.patient?.gender) : null;
      if (templateType === 'womens_health') {
        return WOMENS_GOAL_LABELS[rawGoal] || rawGoal;
      }
      if (templateType === 'mens_health') {
        return MENS_GOAL_LABELS[rawGoal] || rawGoal;
      }
      return rawGoal;
    };
    
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    
    // Handle symptom severity numbers (convert to labels)
    if (typeof value === 'number') {
      const optionLabel = resolveOptionLabel(value);
      if (optionLabel) return optionLabel;
      // Check if this is a symptom field (questionId contains 'symptoms.')
      if (questionId && questionId.startsWith('symptoms.') && value >= 0 && value <= 4) {
        return SEVERITY_LABELS[value] || String(value);
      }
      return String(value);
    }

    if (typeof value === 'string') {
      const optionLabel = resolveOptionLabel(value);
      if (optionLabel) return optionLabel;
    }
    
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-white/30 italic">Not answered</span>;
      const mappedValues =
        questionId && questionId.toLowerCase().includes('selectedgoals')
          ? value.map(item => resolveGoalLabel(String(item)))
          : value;
      return (
        <ul className="list-disc list-inside space-y-1 text-white/80">
          {mappedValues.map((item, idx) => (
            <li key={`${questionId || 'answer'}-${idx}`}>
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </li>
          ))}
        </ul>
      );
    }
    
    if (value === '') return <span className="text-white/30 italic">Not answered</span>;
    
    if (typeof value === 'object') {
      // Handle objects that might not have been flattened
      const entries = Object.entries(value).filter(([_, v]) => v !== null && v !== undefined && v !== '');
      if (entries.length === 0) return <span className="text-white/30 italic">Not answered</span>;
      // Format object entries nicely
      return entries.map(([k, v], idx) => {
        const label = formatKeyToLabel(k);
        const formattedValue = typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v);
        const separator = idx === entries.length - 1 ? '' : ', ';
        return `${label}: ${formattedValue}${separator}`;
      });
    }
    
    const strValue = String(value);
    
    // Check if this is an S3 image key - render as clickable button
    if (isS3ImageKey(strValue)) {
      return (
        <button 
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleViewS3Image(strValue);
          }}
          className="text-blue-400 hover:text-blue-300 underline cursor-pointer bg-transparent border-none p-0"
        >
          View Image
        </button>
      );
    }

    if (isUrl(strValue)) {
      return (
        <a
          href={strValue}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline cursor-pointer"
        >
          View Document
        </a>
      );
    }
    
    // Check if this is a date field (either by questionId or by value format)
    const isDateField = questionId && (
      questionId.toLowerCase().includes('date') || 
      questionId.toLowerCase().includes('dob') ||
      questionId.toLowerCase().includes('birth')
    );
    
    // Check if value looks like a date in YYYY-MM-DD format
    const dateMatch = strValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateMatch || isDateField) {
      if (dateMatch) {
        // Convert YYYY-MM-DD to MM/DD/YYYY
        return `${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1]}`;
      }
    }
    
    return strValue;
  };

  // Helper to detect if responses use stages format
  const isStagesFormat = (responses: any): boolean => {
    return responses && 
           typeof responses === 'object' && 
           Array.isArray(responses.stages) &&
           responses.stages.length > 0;
  };

  const buildFlattenedResponse = (questionId: string, value: unknown): FlattenedResponse => {
    const templateInfo = findTemplateQuestionInfo(questionId, templateLookup);
    return {
      questionId,
      value,
      questionText: templateInfo?.text ?? formatKeyToLabel(questionId),
      questionOrder: templateInfo
        ? templateInfo.sectionIndex * 1000 + templateInfo.questionIndex
        : undefined,
      sectionTitle: templateInfo?.sectionTitle,
      sectionOrder: templateInfo?.sectionIndex,
    };
  };

  // Helper to recursively flatten responses object into array format
  // This ensures ALL questions are displayed, even if they have null/undefined values
  const flattenResponses = (responses: any): FlattenedResponse[] => {
    if (!responses || typeof responses !== 'object') return [];
    
    const flattened: FlattenedResponse[] = [];
    
    // Check if heightFeet and heightInches exist, combine them into single height field
    const hasHeightParts = responses.heightFeet !== undefined || responses.heightInches !== undefined;
    // When shipping_billing exists, flat address keys are redundant duplicates
    const hasShippingBillingObject = responses.shipping_billing != null && typeof responses.shipping_billing === 'object';

    const processObject = (obj: any, prefix: string = '') => {
      Object.entries(obj).forEach(([key, value]) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;

        // Skip consent fields
        if (key.startsWith('consent')) return;
        // Skip address fields anywhere outside shipping_billing when it is the canonical source
        if (hasShippingBillingObject && prefix !== 'shipping_billing' && FLAT_ADDRESS_FIELDS.has(key)) return;
        // Skip explicitly excluded legacy/deprecated fields
        if (EXCLUDED_RESPONSE_PATHS.has(fullKey) || EXCLUDED_RESPONSE_PATHS.has(key)) return;
        // Skip placeholder fields only when they are both:
        // 1) empty (null/undefined/empty string), and
        // 2) not defined in the active intake template.
        if (CONDITIONAL_RESPONSE_PATHS.has(fullKey)) {
          const isEmptyValue = value === null || value === undefined || value === '';
          const hasTemplateQuestion = !!findTemplateQuestionInfo(fullKey, templateLookup);
          if (isEmptyValue && !hasTemplateQuestion) return;
        }
        
        // Skip individual height parts if we're combining them
        if (hasHeightParts && (key === 'heightFeet' || key === 'heightInches')) return;
        
        // Always include the field, even if value is null/undefined/empty
        // This ensures all questions are displayed
        if (value === null || value === undefined) {
          flattened.push(buildFlattenedResponse(fullKey, null));
        } else if (typeof value === 'boolean' || typeof value === 'string' || typeof value === 'number') {
          flattened.push(buildFlattenedResponse(fullKey, value));
        } else if (Array.isArray(value)) {
          // Include array even if empty - shows the question was asked
          flattened.push(buildFlattenedResponse(fullKey, value));
        } else if (typeof value === 'object') {
          // For objects, check if it's empty or has nested properties
          const objectKeys = Object.keys(value);
          if (objectKeys.length === 0) {
            // Empty object - still include it to show the question exists
            flattened.push(buildFlattenedResponse(fullKey, {}));
          } else {
            // Recurse into nested object to get all nested questions
            processObject(value, fullKey);
          }
        }
      });
    };
    
    processObject(responses);
    
    // Add combined height field if both parts exist
    if (hasHeightParts) {
      const feet = responses.heightFeet ?? 0;
      const inches = responses.heightInches ?? 0;
      flattened.push(buildFlattenedResponse('height', `${feet}'${inches}"`));
    }
    
    return flattened;
  };

  // Helper to flatten stages-based responses into question-answer pairs
  const flattenStagesResponses = (stages: any[]): FlattenedResponse[] => {
    const flattened: FlattenedResponse[] = [];
    
    stages.forEach((stage, stageIndex) => {
      const questions = stage.questionnaire?.questions || [];
      
      questions.forEach((question, questionIndex) => {
        const response = question.response;
        
        // Always include the question, even if not answered
        if (!response || !response.answered) {
          flattened.push({
            questionId: question.question_id,
            value: null,
            stageName: stage.stage_name,
            questionText: question.text,
            questionOrder: stageIndex * 1000 + questionIndex,
            sectionTitle: stage.stage_name,
            sectionOrder: stageIndex,
          });
          return;
        }
        
        let value: unknown;
        
        // Handle different question types
        if (question.question_type === 'priority_selector') {
          // Sort selected_options by priority if available, otherwise by order
          const sorted = (response.selected_options || []).sort((a: any, b: any) => {
            if (a.priority !== undefined && b.priority !== undefined) {
              return a.priority - b.priority;
            }
            return (a.order || 0) - (b.order || 0);
          });
          value = sorted.map((opt: any) => opt.text);
        } else if (
          question.question_type === 'multi_select' || 
          question.question_type === 'single_select' ||
          question.question_type === 'checkbox' ||
          question.question_type === 'radio' ||
          question.question_type === 'sub_option_selector_title'
        ) {
          // Extract text from selected options
          value = (response.selected_options || []).map((opt: any) => opt.text);
          if ((question.question_type === 'single_select' || question.question_type === 'radio') && Array.isArray(value) && value.length === 1) {
            value = value[0]; // Convert single-item array to string
          }
        } else if (response.response_text !== null && response.response_text !== undefined) {
          value = response.response_text;
        } else if (response.response_number !== null && response.response_number !== undefined) {
          value = response.response_number;
        } else if (response.selected_options && Array.isArray(response.selected_options) && response.selected_options.length > 0) {
          // Fallback: extract text from selected_options for any unhandled question type
          value = response.selected_options.map((opt: any) => opt.text);
          if (Array.isArray(value) && value.length === 1) {
            value = value[0];
          }
        } else {
          value = null;
        }
        
        flattened.push({
          questionId: question.question_id,
          value,
          stageName: stage.stage_name,
          questionText: question.text,
          questionOrder: stageIndex * 1000 + questionIndex,
          sectionTitle: stage.stage_name,
          sectionOrder: stageIndex,
        });
      });
    });
    
    return flattened;
  };

  // Categorize camelCase fields into logical groups
  const getCategoryForField = (fieldName: string): string => {
    // Personal info fields (including GLP1 specific fields)
    const personalInfoFields = [
      'fullName', 'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 
      'gender', 'biologicalSex', 'height', 'weight', 'age',
      'addressLine1', 'addressLine2', 'city', 'state', 'postalCode',
      'telehealthLocationState', 'governmentIdKey'
    ];
    if (personalInfoFields.includes(fieldName)) return 'personalInfo';
    
    // Weight loss / GLP1 specific fields
    const weightLossFields = [
      'currentWeight', 'goalWeight', 'weightLossGoal', 'weightDistribution', 
      'previousAttempts', 'currentGlp1Type', 'currentGlp1Dose', 
      'currentGlp1Duration', 'currentGlp1SideEffects'
    ];
    if (weightLossFields.includes(fieldName)) return 'weightLossGoals';
    
    // Medical screening (GLP1 specific)
    const medicalScreeningFields = [
      'metabolicConditions', 'contraindications', 'kidneyConditions',
      'pregnancyStatus', 'lastProviderVisit', 'glp1Consent',
      'bloodPressureSystolic', 'bloodPressureDiastolic', 'bloodPressureUnknown'
    ];
    if (medicalScreeningFields.includes(fieldName)) return 'medicalScreening';
    
    // Lifestyle fields
    const lifestyleFields = ['activityLevel', 'exerciseFrequency', 'sleepHours', 'sleepQuality', 'stressLevel', 'alcoholConsumption', 'tobaccoUse', 'diet', 'caffeineCups', 'alcoholDrinks', 'smoking'];
    if (lifestyleFields.includes(fieldName)) return 'lifestyle';
    
    // Medical history fields
    const medicalFields = ['currentMedications', 'medications', 'allergies', 'hasAllergies', 'allergiesDetails', 'chronicConditions', 'medicalConditions', 'surgeries', 'familyHistory', 'previousTRT', 'previousTRTDetails', 'previousHRT', 'previousHRTDetails', 'menopausalStatus', 'birthControlCurrent', 'pregnancies', 'hormoneTherapy', 'previousHormoneTherapy', 'hormoneTherapyDetails', 'additionalMedicalHistory'];
    if (medicalFields.includes(fieldName)) return 'medicalHistory';
    
    // Goals/Preferences fields
    const goalsFields = ['selectedGoals', 'symptoms', 'preferredContactMethod', 'preferredContactTime', 'additionalConcerns'];
    if (goalsFields.includes(fieldName)) return 'goalsPreferences';
    
    // Treatment preferences (Women's intake specific)
    const preferencesFields = ['budget', 'refillFrequency', 'consultationMethod', 'programRecommended', 'additionalPreferences'];
    if (preferencesFields.includes(fieldName)) return 'treatmentPreferences';
    
    // ADAM questionnaire
    if (fieldName.includes('adam') || fieldName.startsWith('adam_')) return 'adam';
    
    // STOP-Bang questionnaire
    if (fieldName.includes('stopBang') || fieldName.startsWith('stopbang_')) return 'stopBang';
    
    return 'other';
  };
  
  // Define field order within each category for consistent display
  const getFieldOrder = (fieldName: string): number => {
    const fieldOrderMap: Record<string, number> = {
      // Personal info - camelCase (Men's/Women's/GLP1 forms)
      fullName: 1,
      firstName: 1,
      lastName: 2,
      dateOfBirth: 3,
      age: 3,
      gender: 4,
      biologicalSex: 4,
      email: 5,
      phone: 6,
      addressLine1: 7,
      addressLine2: 8,
      city: 9,
      state: 10,
      postalCode: 11,
      height: 12,
      weight: 13,
      telehealthLocationState: 14,
      governmentIdKey: 15,
      // Personal info - snake_case (General Health / SmartIntake basic_info.*)
      full_name: 1,
      first_name: 1,
      last_name: 2,
      date_of_birth: 3,
      biological_sex: 4,
      address_line1: 7,
      address_line2: 8,
      postal_code: 11,
      zip_code: 11,
      // Weight loss goals
      currentWeight: 1,
      goalWeight: 2,
      weightLossGoal: 3,
      weightDistribution: 4,
      previousAttempts: 5,
      currentGlp1Type: 6,
      currentGlp1Dose: 7,
      currentGlp1Duration: 8,
      currentGlp1SideEffects: 9,
    };
    return fieldOrderMap[fieldName] ?? 999;
  };

  // Group responses by category for better display
  const groupResponses = (responses: FlattenedResponse[]) => {
    const groups: Record<string, FlattenedResponse[]> = {};
    const categoryOrderMap = new Map<string, number>();

    // Helper: check if a field name (leaf) belongs to billing
    const isBillingLeaf = (leaf: string): boolean =>
      /^billing/i.test(leaf);

    const resolveCategory = (response: FlattenedResponse): string => {
      if (response.stageName) return response.stageName;
      if (!response.questionId) return response.sectionTitle || 'Other';

      const qid = response.questionId;

      // Canonical source: shipping_billing.* nested keys → split into sections
      if (qid.startsWith('shipping_billing.')) {
        const field = qid.split('.')[1] || '';
        return isBillingLeaf(field) ? 'billingAddress' : 'shippingAddress';
      }

      if (response.sectionTitle) {
        // Template-based intakes use sectionTitle "Shipping & Billing Address"
        // for fields like shipping_address_line1, billing_city, etc.
        // Split into separate sections based on the field name.
        if (/shipping.*billing/i.test(response.sectionTitle)) {
          const leaf = qid.split('.').pop() || qid;
          return isBillingLeaf(leaf) ? 'billingAddress' : 'shippingAddress';
        }
        return response.sectionTitle;
      }

      const parts = qid.split('.');
      if (parts.length > 1) {
        return parts[0];
      }
      return getCategoryForField(qid);
    };

    responses.forEach(response => {
      const category = resolveCategory(response);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(response);

      if (response.sectionOrder !== undefined) {
        const current = categoryOrderMap.get(category);
        if (current === undefined || response.sectionOrder < current) {
          categoryOrderMap.set(category, response.sectionOrder);
        }
      }
    });

    // Sort items within each group (respect template/stage order when available)
    Object.keys(groups).forEach(category => {
      groups[category].sort((a, b) => {
        if (a.questionOrder !== undefined || b.questionOrder !== undefined) {
          return (a.questionOrder ?? 9999) - (b.questionOrder ?? 9999);
        }
        const fieldA = (a.questionId || '').split('.').pop() || a.questionId || '';
        const fieldB = (b.questionId || '').split('.').pop() || b.questionId || '';
        return getFieldOrder(fieldA) - getFieldOrder(fieldB);
      });
    });

    return { groups, categoryOrderMap };
  };

  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      basic_info: 'Basic Information',
      personalInfo: 'Personal Information',
      lifestyle: 'Lifestyle',
      medical_history: 'Medical History',
      medicalHistory: 'Medical History',
      medicalScreening: 'Medical Screening',
      weightLossGoals: 'Weight Loss Goals',
      shippingAddress: 'Shipping Address',
      billingAddress: 'Billing Address',
      questionnaires: 'Health Questionnaires',
      goalsPreferences: 'Goals & Preferences',
      treatmentPreferences: 'Treatment Preferences',
      adam: 'ADAM Questionnaire',
      stopBang: 'STOP-Bang Questionnaire',
      other: 'Other Information'
    };
    return labels[category] || category.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());
  };
  
  // Define the order of categories
  const categoryOrder = [
    'personalInfo', 'basic_info', 'weightLossGoals', 'medicalScreening',
    'lifestyle', 'medicalHistory', 'medical_history', 'goalsPreferences',
    'treatmentPreferences', 'shippingAddress', 'billingAddress',
    'questionnaires', 'adam', 'stopBang',
    // Stage names (in typical order)
    'Health History', 'Symptoms', 'Lifestyle', 'Goals',
    'other'
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl bg-[#0A0F1A] border-l border-white/10 z-[450] p-6">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Review Response
          </SheetTitle>
          <SheetDescription className="text-white/60">
            Review the patient's submitted intake responses
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : data ? (
          <ScrollArea className="h-[calc(100vh-200px)] mt-6 pr-4">
            <div className="space-y-6">
              {/* Patient Info */}
              {data.patient && (
                <div className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-blue-500/10">
                      <User className="w-4 h-4 text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-white">
                        {data.patient.name
                          ? data.patient.name.replace(/\b\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                          : data.patient.name}
                      </h4>
                      <p className="text-xs text-white/50">
                        {(() => {
                          const gender = data.patient.gender;
                          if (!gender) return 'Not specified';
                          // Handle numeric gender values
                          if (typeof gender === 'number' || !isNaN(Number(gender))) {
                            const genderMap: Record<string, string> = { '1': 'Male', '2': 'Female', '3': 'Unknown' };
                            return genderMap[String(gender)] || 'Not specified';
                          }
                          // Handle string gender values
                          const genderStr = String(gender);
                          return genderStr.charAt(0).toUpperCase() + genderStr.slice(1).toLowerCase();
                        })()} · DOB: {data.patient.dateOfBirth
                          ? (() => {
                              const dob = String(data.patient.dateOfBirth);
                              const datePart = dob.split('T')[0];
                              const parts = datePart.split('-');
                              return parts.length === 3 ? `${parts[1]}/${parts[2]}/${parts[0]}` : dob;
                            })()
                          : 'Not provided'
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-white/40">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      Completed {formatDate(data.completedAt)}
                    </span>
                    <Badge className="bg-white/5 text-white/60 border-white/10">
                      {data.intakeFormTemplate?.name
                        ? `Custom · ${data.intakeFormTemplate.name}`
                        : getTherapyCategoryLabel(data.therapyCategory)}
                    </Badge>
                  </div>
                </div>
              )}

              {/* Scores Section */}
              {data.scores && Object.keys(data.scores).length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-blue-500" />
                    Calculated Scores
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(data.scores).map(([name, value]) => (
                      <div
                        key={name}
                        className={`p-3 rounded-lg ${getScoreColor(name, value as number)}`}
                      >
                        <div className="text-xs font-medium uppercase opacity-80">
                          {name.replace(/_/g, ' ')}
                        </div>
                        <div className="text-2xl font-bold">{value}</div>
                        <div className="text-xs opacity-70">
                          {getScoreLabel(name, value as number)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Flags */}
              {data.riskFlags && Object.values(data.riskFlags).some(v => v) && (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                  <h4 className="text-sm font-medium text-red-400 flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Risk Flags Detected
                  </h4>
                  <ul className="space-y-1">
                    {Object.entries(data.riskFlags)
                      .filter(([, value]) => value)
                      .map(([flag]) => (
                        <li key={flag} className="text-sm text-red-300 flex items-center gap-2">
                          <AlertCircle className="w-3 h-3" />
                          {flag.replace(/_/g, ' ')}
                        </li>
                      ))}
                  </ul>
                </div>
              )}

              {/* Responses */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-white flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-500" />
                  Response Details
                </h4>
                {(() => {
                  // Handle stages format, flat object, or array formats from backend
                  const responsesData = (() => {
                    // Handle stages format
                    if (isStagesFormat(data.responses)) {
                      const stagesResponse = data.responses as { stages: any[] };
                      return flattenStagesResponses(stagesResponse.stages);
                    }
                    // Handle flat object or array formats
                    return Array.isArray(data.responses) 
                      ? (data.responses as Array<{ questionId: string; value: unknown }>).map(item => ({
                          ...buildFlattenedResponse(item.questionId, item.value),
                          ...item,
                        }))
                      : flattenResponses(data.responses as Record<string, unknown>);
                  })();
                  
                  const { groups: groupedResponses, categoryOrderMap } = groupResponses(responsesData);
                  // Sort categories by defined order
                  const categories = Object.keys(groupedResponses).sort((a, b) => {
                    const topPriority = ['basic_info', 'personalInfo'];
                    const topA = topPriority.indexOf(a);
                    const topB = topPriority.indexOf(b);
                    if (topA !== -1 || topB !== -1) {
                      return (topA === -1 ? 999 : topA) - (topB === -1 ? 999 : topB);
                    }

                    const templateOrderA = categoryOrderMap.get(a);
                    const templateOrderB = categoryOrderMap.get(b);
                    if (templateOrderA !== undefined || templateOrderB !== undefined) {
                      return (templateOrderA ?? 999) - (templateOrderB ?? 999);
                    }
                    const orderA = categoryOrder.indexOf(a);
                    const orderB = categoryOrder.indexOf(b);
                    // If not in categoryOrder, put at end
                    return (orderA === -1 ? 999 : orderA) - (orderB === -1 ? 999 : orderB);
                  });
                  
                  return categories.length > 0 ? (
                    <div className="space-y-4">
                      {categories.map(category => (
                        <div key={category} className="space-y-2">
                          <h5 className="text-xs font-medium text-white/60 uppercase tracking-wider mb-3">
                            {getCategoryLabel(category)}
                          </h5>
                          <div className="space-y-2">
                            {groupedResponses[category].map((response, idx) => {
                              // Always show the question, even if empty - ensures all questions are visible
                              return (
                                <div
                                  key={`${category}-${idx}-${response.questionId}`}
                                  className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] transition-colors"
                                >
                                  <div
                                    className="text-xs font-medium text-white/60 mb-1.5 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5"
                                    dangerouslySetInnerHTML={{
                                      __html: response.questionText || formatKeyToLabel(response.questionId)
                                    }}
                                  />
                                  <div className="text-sm text-white font-normal">
                                    {formatQuestionValue(response.value, response.questionId)}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.06] text-center">
                      <p className="text-sm text-white/40">No responses recorded</p>
                    </div>
                  );
                })()}
              </div>

              {/* Review Notes */}
              {!data.isReviewed && (
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-white">Review Notes (Optional)</h4>
                  <textarea
                    placeholder="Add notes about this response..."
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows={3}
                    className="dark-textarea w-full rounded-md px-3 py-2 text-sm"
                  />
                </div>
              )}

              {/* Already Reviewed */}
              {data.isReviewed && (
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 text-green-400 mb-2">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-sm font-medium">Reviewed</span>
                  </div>
                  <p className="text-xs text-green-300/70">
                    By {data.reviewedBy?.name} on {data.reviewedAt && formatDate(data.reviewedAt)}
                  </p>
                  {data.reviewNotes && (
                    <p className="text-sm text-white/70 mt-2 italic">"{data.reviewNotes}"</p>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-white/40">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p>No response data available</p>
          </div>
        )}

        {/* Action Buttons */}
        {data && !data.isReviewed && (
          <div className="flex gap-3 mt-6 pt-4 border-t border-white/10">
            <Button
              onClick={handleMarkReviewed}
              disabled={isSubmitting}
              className="flex-1 bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 border border-blue-500/20"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Mark as Reviewed
            </Button>
            {onRunIntelliHealth && (
              <Button
                onClick={() => {
                  onRunIntelliHealth();
                  onOpenChange(false);
                }}
                className="flex-1 bg-blue-500 text-white hover:bg-blue-600"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Run IntelliHealth
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default IntakeResponseReviewDrawer;